import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FileSignature } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { CampoData, Entrada, Rotulo, Selecao } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { get, getTodos, mensagemDeErro, post } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';

/** D24: o que `GET /documentos/contratos/preparar` devolve (tipos da API do Documentos; a tela só lê). */
interface CampoPreparado {
  indice: number;
  chave: string;
  rotulo: string;
  obrigatorio: boolean;
  origem: 'sistema' | 'envio';
  valor: string | null;
}
interface Preparo {
  sugeridos: { id: string; nome: string; versao: number }[];
  outros: { id: string; nome: string; versao: number }[];
  modelo_id: string | null;
  versao: number | null;
  socios: { indice: number; nome: string }[];
  campos: CampoPreparado[];
  pdf: boolean;
  /** L3a (aditivo ou termo): o plano atual, se é contrato antigo e o motivo de a mudança não poder valer na assinatura. */
  plano_id: string | null;
  /** L3b: a empresa do contrato (a cessão escolhe outra). */
  empresa_id: string | null;
  antigo: boolean;
  na_assinatura: string | null;
}

/** D24 L3a: eventos do aditivo ou termo gerado a partir do contrato; L3b: a cessão; L3c: o termo de inadimplência (avulso). */
const EVENTOS = ['suspensao', 'reativacao', 'migracao_plano', 'troca_regime', 'rescisao', 'cessao', 'rescisao_inadimplencia'] as const;
type Evento = (typeof EVENTOS)[number];

/** Campos do mesmo dado (fora o "outro") se preenchem uma vez só: o grupo leva todas as posições. */
function agrupar(campos: CampoPreparado[]) {
  const grupos = new Map<string, { chave: string; rotulo: string; obrigatorio: boolean; origem: 'sistema' | 'envio'; valor: string; indices: number[] }>();
  for (const c of campos) {
    const k = c.chave === 'outro' ? `#${c.indice}` : c.chave;
    const g = grupos.get(k);
    if (g) {
      g.indices.push(c.indice);
      g.obrigatorio ||= c.obrigatorio;
    } else grupos.set(k, { chave: k, rotulo: c.rotulo, obrigatorio: c.obrigatorio, origem: c.origem, valor: c.valor ?? '', indices: [c.indice] });
  }
  // Primeiro o que falta preencher, depois o que veio do sistema (dá para corrigir).
  return [...grupos.values()].sort((a, b) => Number(!!a.valor) - Number(!!b.valor));
}

/**
 * D24: gera o documento do contrato a partir do modelo .docx do Documentos. O sistema escolhe o modelo (empresa, serviços e
 * regime do cliente) e a pessoa pode trocar; os campos vêm preenchidos do contrato, do cliente e da empresa, e o que não tem
 * origem no sistema se preenche aqui. O documento nasce ligado ao contrato, em Documentos e assinatura > Contratos.
 *
 * L3a (`aditivo`): o aditivo ou termo do evento (suspensão, reativação, migração de plano, troca de regime, rescisão sem
 * débitos), com a data e o plano novo (padrão dos campos), e quando a mudança vale: já (o documento só formaliza) ou na
 * assinatura (o sistema aplica quando todos assinarem).
 */
export function GerarDocumento({
  contratoId,
  aoFechar,
  aditivo,
  estado,
  inadimplente,
}: {
  contratoId: string;
  aoFechar: () => void;
  aditivo?: boolean;
  estado?: string;
  /** L3c: o termo de rescisão por inadimplência só aparece para o contrato inadimplente. */
  inadimplente?: boolean;
}) {
  const { t } = useTranslation('assinaturas');
  const qc = useQueryClient();
  const toast = useToast();
  const navegar = useNavigate();
  const id = useMemo(() => crypto.randomUUID(), []);
  const [modeloId, setModeloId] = useState<string | null>(null);
  const [garantidor, setGarantidor] = useState(0);
  const [editados, setEditados] = useState<Record<string, string>>({});
  const [evento, setEvento] = useState<Evento>(estado === 'pausado' ? 'reativacao' : 'suspensao');
  const [dataEfeito, setDataEfeito] = useState('');
  const [planoId, setPlanoId] = useState('');
  const [destinoId, setDestinoId] = useState('');
  const sessao = useSessao();
  const [efeito, setEfeito] = useState<'ja' | 'assinatura'>('ja');
  const mudanca = aditivo ? { evento, ...(dataEfeito ? { data: dataEfeito } : {}), ...(evento === 'migracao_plano' && planoId ? { plano_id: planoId } : {}), ...(evento === 'cessao' && destinoId ? { empresa_destino_id: destinoId } : {}) } : {};
  const preparo = useQuery({
    queryKey: ['documentos', 'preparar', contratoId, modeloId, garantidor, mudanca],
    queryFn: () => get<Preparo>('/documentos/contratos/preparar', { contrato_id: contratoId, ...(modeloId ? { modelo_id: modeloId } : {}), garantidor, ...mudanca }),
    placeholderData: keepPreviousData,
  });
  const planos = useQuery({
    queryKey: ['assinaturas', 'planos', 'ativos'],
    queryFn: () => getTodos<{ id: string; nome: string }>('/assinaturas/planos', { ativo: true }),
    enabled: !!aditivo && evento === 'migracao_plano',
  });
  const p = preparo.data;
  const grupos = useMemo(() => agrupar(p?.campos ?? []), [p]);
  const valorDe = (g: (typeof grupos)[number]) => editados[g.chave] ?? g.valor;
  const gerar = useMutation({
    mutationFn: () => {
      const valores: Record<string, string> = {};
      for (const g of grupos) for (const i of g.indices) valores[String(i)] = valorDe(g);
      const extra = aditivo ? { ...mudanca, efeito: podeNaAssinatura ? efeito : 'ja' } : {};
      return post<{ id: string; pdf: boolean }>('/documentos/contratos/docx', { id, contrato_id: contratoId, modelo_id: p!.modelo_id, valores, garantidor, ...extra }, { 'Idempotency-Key': id });
    },
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['documentos'] });
      if (aditivo && podeNaAssinatura && efeito === 'assinatura') toast.sucesso(t('aditivo.geradoPendente'));
      else toast.sucesso(r.pdf ? t('documento.gerado') : t('documento.geradoSemPdf'));
      aoFechar();
      navegar(`/cadastros/documentos/contratos?id=${r.id}`);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const pendentes = grupos.filter((g) => g.obrigatorio && !valorDe(g).trim()).length;
  const motivoSemAssinatura = evento === 'troca_regime' ? t('aditivo.soFormaliza') : (p?.na_assinatura ?? null);
  const podeNaAssinatura = !motivoSemAssinatura;
  // A migração sempre pede o plano (o novo valor sai dele); a cessão pede a empresa só para valer na assinatura.
  const faltaPlano = !!aditivo && ((evento === 'migracao_plano' && !planoId) || (podeNaAssinatura && efeito === 'assinatura' && evento === 'cessao' && !destinoId));
  const trocarMudanca = (f: () => void) => {
    setEditados({});
    setModeloId(null);
    f();
  };
  const opcoesModelo = p
    ? [...p.sugeridos.map((m, i) => ({ valor: m.id, rotulo: i === 0 ? t('documento.sugerido', { nome: m.nome }) : m.nome })), ...p.outros.map((m) => ({ valor: m.id, rotulo: m.nome }))]
    : [];

  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={aditivo ? t('aditivo.titulo') : t('documento.titulo')}
      rodape={
        <Botao variante="primario" icone={<FileSignature className="size-4" />} carregando={gerar.isPending} disabled={!p?.modelo_id || pendentes > 0 || faltaPlano} onClick={() => gerar.mutate()}>
          {aditivo ? t('aditivo.gerar') : t('documento.gerar')}
        </Botao>
      }
    >
      {aditivo && (
        <div className="mb-4 flex min-w-0 flex-col gap-4">
          <Rotulo id="aditivo-evento" rotulo={t('aditivo.evento')} ajuda={p?.antigo ? t('aditivo.antigo') : undefined}>
            <Selecao id="aditivo-evento" value={evento} opcoes={EVENTOS.filter((v) => v !== 'rescisao_inadimplencia' || inadimplente).map((v) => ({ valor: v, rotulo: t(`aditivo.eventos.${v}`) }))} onChange={(e) => trocarMudanca(() => setEvento(e.target.value as Evento))} />
          </Rotulo>
          {evento !== 'rescisao_inadimplencia' && (
            <Rotulo id="aditivo-data" rotulo={evento === 'rescisao' ? t('aditivo.dataRescisao') : evento === 'cessao' ? t('aditivo.dataCessao') : t('aditivo.data')} ajuda={t('aditivo.dataAjuda')}>
              <CampoData id="aditivo-data" type="date" value={dataEfeito} onChange={(e) => trocarMudanca(() => setDataEfeito(e.target.value))} />
            </Rotulo>
          )}
          {evento === 'migracao_plano' && (
            <Rotulo id="aditivo-plano" rotulo={t('aditivo.plano')} ajuda={t('aditivo.planoAjuda')}>
              <Selecao
                id="aditivo-plano"
                value={planoId}
                vazio={t('aditivo.escolhaPlano')}
                opcoes={(planos.data?.itens ?? []).filter((x) => x.id !== p?.plano_id).map((x) => ({ valor: x.id, rotulo: x.nome }))}
                onChange={(e) => trocarMudanca(() => setPlanoId(e.target.value))}
              />
            </Rotulo>
          )}
          {evento === 'cessao' && (
            <Rotulo id="aditivo-destino" rotulo={t('aditivo.destino')} ajuda={t('aditivo.destinoAjuda')}>
              <Selecao
                id="aditivo-destino"
                value={destinoId}
                vazio={t('aditivo.escolhaDestino')}
                opcoes={(sessao.empresas?.opcoes ?? []).filter((x) => x.id !== p?.empresa_id).map((x) => ({ valor: x.id, rotulo: x.nome }))}
                onChange={(e) => trocarMudanca(() => setDestinoId(e.target.value))}
              />
            </Rotulo>
          )}
          <Rotulo id="aditivo-efeito" rotulo={t('aditivo.efeito')} ajuda={motivoSemAssinatura ?? (faltaPlano ? t(evento === 'cessao' ? 'aditivo.faltaDestino' : 'aditivo.faltaPlano') : t(`aditivo.ajuda.${efeito}`))}>
            <Selecao
              id="aditivo-efeito"
              value={podeNaAssinatura ? efeito : 'ja'}
              disabled={!podeNaAssinatura}
              opcoes={(['ja', 'assinatura'] as const).map((v) => ({ valor: v, rotulo: t(`aditivo.efeitos.${v}`) }))}
              onChange={(e) => setEfeito(e.target.value as 'ja' | 'assinatura')}
            />
          </Rotulo>
        </div>
      )}
      {preparo.isPending ? (
        <Carregando />
      ) : preparo.isError ? (
        <Erro erro={preparo.error} aoTentar={() => preparo.refetch()} />
      ) : !p!.sugeridos.length && !p!.outros.length ? (
        <Vazio texto={aditivo ? t('aditivo.semModelo') : t('documento.semModelo')} />
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          <p className="text-sm text-tinta-3">{p!.pdf ? t('documento.ajuda') : t('documento.ajudaSemPdf')}</p>
          <Rotulo id="doc-modelo" rotulo={t('documento.modelo')} ajuda={p!.modelo_id && p!.versao ? t('documento.versao', { versao: p!.versao }) : t('documento.escolha')}>
            <Selecao
              id="doc-modelo"
              value={p!.modelo_id ?? ''}
              vazio={t('documento.escolha')}
              opcoes={opcoesModelo}
              onChange={(e) => {
                setEditados({});
                setModeloId(e.target.value || null);
              }}
            />
          </Rotulo>
          {p!.socios.length > 1 && (
            <Rotulo id="doc-garantidor" rotulo={t('documento.garantidor')} ajuda={t('documento.garantidorAjuda')}>
              <Selecao id="doc-garantidor" value={String(garantidor)} opcoes={p!.socios.map((x) => ({ valor: String(x.indice), rotulo: x.nome }))} onChange={(e) => setGarantidor(Number(e.target.value))} />
            </Rotulo>
          )}
          {p!.modelo_id && (
            <section className="flex min-w-0 flex-col gap-3" aria-label={t('documento.campos')}>
              <span className="text-sm font-medium text-tinta-2">{t('documento.campos')}</span>
              {grupos.map((g) => (
                <Rotulo key={g.chave} id={`doc-${g.chave}`} rotulo={g.rotulo} obrigatorio={g.obrigatorio} ajuda={g.origem === 'sistema' && g.valor ? t('documento.doSistema') : undefined}>
                  <Entrada id={`doc-${g.chave}`} value={valorDe(g)} maxLength={500} onChange={(e) => setEditados((x) => ({ ...x, [g.chave]: e.target.value }))} />
                </Rotulo>
              ))}
              {pendentes > 0 && <p className="text-xs text-tinta-3">{t('documento.pendentes', { count: pendentes })}</p>}
            </section>
          )}
        </div>
      )}
    </Drawer>
  );
}
